import { useState, useEffect, useRef, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useDashboardStore } from '../../store/dashboardStore';
import { useCreditCosts } from '../../hooks/useCreditCosts';
import ReactECharts from 'echarts-for-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import {
  PaperAirplaneIcon,
  SparklesIcon,
  UserIcon,
  PlusIcon,
  ChevronDownIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  Bars3Icon,
  ArrowDownTrayIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import html2pdf from 'html2pdf.js';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import ThinkingChain from '../../components/common/ThinkingChain';
import SampleQuestions from '../../components/ai/SampleQuestions';
import QueryPatterns from '../../components/ai/QueryPatterns';

// Chart types for the Add to Dashboard modal
const chartTypes = [
  { id: 'bar', name: 'Bar Chart', icon: '📊' },
  { id: 'line', name: 'Line Chart', icon: '📈' },
  { id: 'pie', name: 'Pie Chart', icon: '🥧' },
  { id: 'donut', name: 'Donut Chart', icon: '🍩' },
  { id: 'area', name: 'Area Chart', icon: '📉' },
  { id: 'scatter', name: 'Scatter Plot', icon: '🔵' },
  { id: 'heatmap', name: 'Heatmap', icon: '🔥' },
  { id: 'geo', name: 'Geo Map', icon: '🌍' },
  { id: 'funnel', name: 'Funnel Chart', icon: '🔻' },
  { id: 'table', name: 'Table', icon: '📋' },
  { id: 'kpi', name: 'KPI Card', icon: '🎯' }
];

// Build ECharts options from chart data and config
const buildChartPreviewOption = (chartData, chartType, config) => {
  if (!chartData || chartData.length === 0) return null;
  
  const baseOption = {
    tooltip: { trigger: chartType === 'pie' || chartType === 'donut' ? 'item' : 'axis' },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '8%', containLabel: true },
    backgroundColor: 'transparent'
  };
  
  switch (chartType) {
    case 'bar':
    case 'line':
    case 'area': {
      const xField = config?.xAxis?.field || Object.keys(chartData[0])[0];
      const yField = config?.series?.[0]?.field || Object.keys(chartData[0])[1];
      return {
        ...baseOption,
        xAxis: {
          type: 'category',
          data: chartData.map(d => d[xField]),
          axisLabel: { color: '#666', rotate: chartData.length > 10 ? 45 : 0 }
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: '#666' }
        },
        series: [{
          name: config?.series?.[0]?.name || yField,
          type: chartType === 'area' ? 'line' : chartType,
          data: chartData.map(d => d[yField]),
          areaStyle: chartType === 'area' ? { opacity: 0.3 } : undefined,
          itemStyle: { 
            color: '#6366f1',
            borderRadius: chartType === 'bar' ? [4, 4, 0, 0] : undefined
          },
          smooth: chartType === 'line' || chartType === 'area'
        }]
      };
    }
    
    case 'pie':
    case 'donut': {
      const nameField = config?.xAxis?.field || Object.keys(chartData[0])[0];
      const valueField = config?.series?.[0]?.field || Object.keys(chartData[0])[1];
      return {
        ...baseOption,
        series: [{
          type: 'pie',
          radius: chartType === 'donut' ? ['40%', '70%'] : '70%',
          data: chartData.map(d => ({
            name: d[nameField],
            value: d[valueField]
          })),
          label: { color: '#666' }
        }]
      };
    }
    
    case 'scatter': {
      const xField = config?.xAxis?.field || Object.keys(chartData[0])[0];
      const yField = config?.series?.[0]?.field || Object.keys(chartData[0])[1];
      return {
        ...baseOption,
        xAxis: { type: 'value', axisLabel: { color: '#666' } },
        yAxis: { type: 'value', axisLabel: { color: '#666' } },
        series: [{
          type: 'scatter',
          data: chartData.map(d => [d[xField], d[yField]]),
          itemStyle: { color: '#6366f1' }
        }]
      };
    }
    
    case 'kpi': {
      const valueField = Object.keys(chartData[0])[0];
      const value = chartData[0]?.[valueField];
      return {
        ...baseOption,
        series: [{
          type: 'gauge',
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max: typeof value === 'number' ? value * 2 : 100,
          pointer: { show: false },
          progress: { show: true, width: 20, itemStyle: { color: '#6366f1' } },
          axisLine: { lineStyle: { width: 20 } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          detail: {
            fontSize: 24,
            fontWeight: 'bold',
            color: '#333',
            offsetCenter: [0, '0%'],
            formatter: typeof value === 'number' ? (value >= 1000 ? `${(value/1000).toFixed(1)}K` : value.toFixed(0)) : value
          },
          data: [{ value: typeof value === 'number' ? value : 0 }]
        }]
      };
    }
    
    default:
      return null;
  }
};

// Export message as PDF
const exportToPDF = async (messageContent, messageIndex) => {
  // Create a temporary container for the PDF content
  const container = document.createElement('div');
  container.innerHTML = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px;">
      <div style="border-bottom: 2px solid #6366f1; padding-bottom: 16px; margin-bottom: 24px;">
        <h1 style="color: #6366f1; margin: 0; font-size: 24px;">AI Assistant Response</h1>
        <p style="color: #666; margin: 8px 0 0 0; font-size: 12px;">Exported on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
      </div>
      <div style="line-height: 1.6; color: #333;">
        ${messageContent.replace(/\n/g, '<br/>')}
      </div>
    </div>
  `;
  
  const options = {
    margin: [10, 10, 10, 10],
    filename: `ai-response-${messageIndex + 1}-${new Date().toISOString().slice(0, 10)}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
  };
  
  try {
    await html2pdf().set(options).from(container).save();
    return true;
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

// Export message as DOC
const exportToDoc = async (messageContent, messageIndex) => {
  // Parse the markdown content into paragraphs
  const lines = messageContent.split('\n');
  const children = [];
  
  // Add title
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'AI Assistant Response',
          bold: true,
          size: 48,
          color: '6366f1'
        })
      ],
      heading: HeadingLevel.TITLE,
      spacing: { after: 200 }
    })
  );
  
  // Add export date
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Exported on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
          size: 20,
          color: '666666',
          italics: true
        })
      ],
      spacing: { after: 400 }
    })
  );
  
  // Add content paragraphs
  lines.forEach((line) => {
    if (line.trim() === '') {
      children.push(new Paragraph({ text: '' }));
    } else if (line.startsWith('# ')) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: line.replace(/^# /, ''), bold: true, size: 36 })],
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 200, after: 100 }
        })
      );
    } else if (line.startsWith('## ')) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: line.replace(/^## /, ''), bold: true, size: 32 })],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        })
      );
    } else if (line.startsWith('### ')) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: line.replace(/^### /, ''), bold: true, size: 28 })],
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 100 }
        })
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: line.replace(/^[-*] /, ''), size: 24 })],
          bullet: { level: 0 },
          spacing: { before: 50, after: 50 }
        })
      );
    } else if (/^\d+\. /.test(line)) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: line.replace(/^\d+\. /, ''), size: 24 })],
          numbering: { reference: 'numbered-list', level: 0 },
          spacing: { before: 50, after: 50 }
        })
      );
    } else if (line.startsWith('```')) {
      // Skip code block markers
    } else if (line.startsWith('**') && line.endsWith('**')) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: line.replace(/\*\*/g, ''), bold: true, size: 24 })],
          spacing: { before: 100, after: 100 }
        })
      );
    } else {
      // Handle inline bold and code
      const runs = [];
      let remaining = line;
      const boldRegex = /\*\*([^*]+)\*\*/g;
      const codeRegex = /`([^`]+)`/g;
      let lastIndex = 0;
      
      // Simple text for now - can be enhanced for inline formatting
      runs.push(new TextRun({ text: line, size: 24 }));
      
      children.push(
        new Paragraph({
          children: runs,
          spacing: { before: 50, after: 50 }
        })
      );
    }
  });
  
  const doc = new Document({
    sections: [{
      properties: {},
      children: children
    }],
    numbering: {
      config: [{
        reference: 'numbered-list',
        levels: [{
          level: 0,
          format: 'decimal',
          text: '%1.',
          alignment: 'start',
          style: { paragraph: { indent: { left: 720, hanging: 260 } } }
        }]
      }]
    }
  });
  
  try {
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `ai-response-${messageIndex + 1}-${new Date().toISOString().slice(0, 10)}.docx`);
    return true;
  } catch (error) {
    console.error('DOC export error:', error);
    throw error;
  }
};

// Export Dropdown Component
const ExportDropdown = ({ messageContent, messageIndex, onExport }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleExport = async (format) => {
    setIsOpen(false);
    try {
      if (format === 'pdf') {
        await exportToPDF(messageContent, messageIndex);
        toast.success('Exported as PDF');
      } else if (format === 'doc') {
        await exportToDoc(messageContent, messageIndex);
        toast.success('Exported as Word document');
      }
    } catch (error) {
      toast.error(`Failed to export: ${error.message}`);
    }
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1.5 rounded-lg text-surface-400 hover:text-primary-500 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
        title="Export response"
      >
        <ArrowDownTrayIcon className="w-4 h-4" />
      </button>
      
      <Transition
        show={isOpen}
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <div className="absolute left-0 bottom-full mb-1 w-40 rounded-lg bg-white dark:bg-surface-800 shadow-lg ring-1 ring-surface-200 dark:ring-surface-700 z-[100]">
          <div className="py-1">
            <button
              onClick={() => handleExport('pdf')}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700"
            >
              <DocumentIcon className="w-4 h-4 text-red-500" />
              Export as PDF
            </button>
            <button
              onClick={() => handleExport('doc')}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700"
            >
              <DocumentIcon className="w-4 h-4 text-blue-500" />
              Export as Word
            </button>
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default function AIChat() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, getCurrentCompanyId, isSuperUser } = useAuthStore();
  const { formatCost } = useCreditCosts();
  const messagesEndRef = useRef(null);
  
  const [message, setMessage] = useState('');
  const [provider, setProvider] = useState(user?.preferences?.defaultAIProvider || 'anthropic');
  const [model, setModel] = useState('');
  const [messages, setMessages] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(conversationId);
  
  // Mobile sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Add to Dashboard modal state
  const [showAddToDashboard, setShowAddToDashboard] = useState(false);
  const [selectedChartSuggestion, setSelectedChartSuggestion] = useState(null);
  const [selectedDashboard, setSelectedDashboard] = useState('');
  const [selectedChartType, setSelectedChartType] = useState('');
  const [chartName, setChartName] = useState('');
  const [isAddingChart, setIsAddingChart] = useState(false);
  
  // Thinking chain state
  const [thinkingStep, setThinkingStep] = useState(0);
  const [thinkingStartTime, setThinkingStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Query patterns helper
  const [showQueryPatterns, setShowQueryPatterns] = useState(false);
  const [stepDetails, setStepDetails] = useState({});
  const [isStreaming, setIsStreaming] = useState(false);
  
  const { addChart } = useDashboardStore();
  
  // Fetch available models
  const { data: modelsData } = useQuery({
    queryKey: ['aiModels'],
    queryFn: () => apiHelpers.getModels()
  });
  
  // Get current company ID for filtering (important for Super/Managing Admins)
  const currentCompanyId = getCurrentCompanyId();
  
  // Check if company is selected (for Super/Managing Admins)
  const noCompanySelected = isSuperUser() && !currentCompanyId;
  
  // Fetch conversations list - filter by selected company for Super/Managing Admins
  // Include user._id in queryKey to ensure refetch when user changes (account switch)
  const { data: conversationsData, refetch: refetchConversations } = useQuery({
    queryKey: ['conversations', user?._id, currentCompanyId],
    queryFn: () => apiHelpers.getConversations({ companyId: currentCompanyId }),
    enabled: !!user?._id && (!!currentCompanyId || !isSuperUser()) // Regular users always enabled, super users need company selected
  });
  
  // Fetch current conversation
  const { data: conversationData } = useQuery({
    queryKey: ['conversation', currentConversationId],
    queryFn: () => apiHelpers.getConversation(currentConversationId),
    enabled: !!currentConversationId
  });
  
  // Fetch dashboards for Add to Dashboard modal
  const { data: dashboardsData } = useQuery({
    queryKey: ['dashboards', currentCompanyId],
    queryFn: () => apiHelpers.getDashboards({ companyId: currentCompanyId }),
    enabled: showAddToDashboard && !!currentCompanyId
  });
  
  useEffect(() => {
    if (conversationData?.data?.conversation?.messages) {
      setMessages(conversationData.data.conversation.messages);
    }
  }, [conversationData]);
  
  // Clear state when user changes (account switch) to prevent showing previous user's data
  useEffect(() => {
    setMessages([]);
    setCurrentConversationId(null);
    // Navigate to base AI route to clear any conversation ID from URL
    if (conversationId) {
      navigate('/ai', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);
  
  useEffect(() => {
    if (modelsData?.data?.providers?.[provider]) {
      setModel(modelsData.data.providers[provider].defaultModel);
    }
  }, [provider, modelsData]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Scroll when thinking step changes
  useEffect(() => {
    if (thinkingStep > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [thinkingStep]);
  
  // Elapsed time tracking effect
  useEffect(() => {
    if (!thinkingStartTime) return;
    
    // Update elapsed time every 100ms
    const timeInterval = setInterval(() => {
      setElapsedTime(Date.now() - thinkingStartTime);
    }, 100);
    
    return () => {
      clearInterval(timeInterval);
    };
  }, [thinkingStartTime]);
  
  // Handle progress updates from streaming API
  const handleProgress = (progressData) => {
    const { step, details } = progressData;
    setThinkingStep(step);
    
    if (details) {
      setStepDetails(prev => ({ ...prev, [step]: details }));
    }
  };
  
  // Streaming chat function
  const sendStreamingChat = async (payload) => {
    setIsStreaming(true);
    
    try {
      const response = await apiHelpers.chatStream(payload, handleProgress);
      const { response: aiResponse, conversationId: newConvId, chartSuggestion, chartData } = response.data;
      
      // Reset thinking chain
      setThinkingStep(0);
      setThinkingStartTime(null);
      setElapsedTime(0);
      setStepDetails({});
      setIsStreaming(false);
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: aiResponse,
        chartSuggestion,
        chartData
      }]);
      
      if (!currentConversationId && newConvId) {
        setCurrentConversationId(newConvId);
        navigate(`/ai/${newConvId}`, { replace: true });
        refetchConversations();
      }
    } catch (error) {
      // Reset thinking chain on error
      setThinkingStep(0);
      setThinkingStartTime(null);
      setElapsedTime(0);
      setStepDetails({});
      setIsStreaming(false);
      
      toast.error(error.message || 'Failed to send message');
    }
  };
  
  // Keep the mutation for backwards compatibility (non-streaming)
  const chatMutation = useMutation({
    mutationFn: (data) => apiHelpers.chat(data),
    onSuccess: (response) => {
      const { response: aiResponse, conversationId: newConvId, chartSuggestion, chartData } = response.data;
      
      // Reset thinking chain
      setThinkingStep(0);
      setThinkingStartTime(null);
      setElapsedTime(0);
      setStepDetails({});
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: aiResponse,
        chartSuggestion,
        chartData
      }]);
      
      if (!currentConversationId && newConvId) {
        setCurrentConversationId(newConvId);
        navigate(`/ai/${newConvId}`, { replace: true });
        refetchConversations();
      }
    },
    onError: (error) => {
      // Reset thinking chain on error
      setThinkingStep(0);
      setThinkingStartTime(null);
      setElapsedTime(0);
      setStepDetails({});
      
      toast.error(error.response?.data?.error || 'Failed to send message');
    }
  });
  
  const deleteMutation = useMutation({
    mutationFn: (id) => apiHelpers.deleteConversation(id),
    onSuccess: (_, deletedId) => {
      toast.success('Conversation deleted');
      refetchConversations();
      
      // If we deleted the current conversation, start a new one
      if (currentConversationId === deletedId) {
        startNewConversation();
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete conversation');
    }
  });
  
  const handleDeleteConversation = (e, convId) => {
    e.stopPropagation(); // Prevent selecting the conversation
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      deleteMutation.mutate(convId);
    }
  };
  
  const handleSend = () => {
    if (!message.trim() || isStreaming || chatMutation.isPending) return;
    
    const userMessage = message.trim();
    setMessage('');
    
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    // Start thinking chain
    setThinkingStep(0);
    setThinkingStartTime(Date.now());
    setElapsedTime(0);
    setStepDetails({});
    
    // Only include conversationId if it exists (null/undefined would cause validation error)
    const payload = {
      message: userMessage,
      provider,
      model,
      companyId: currentCompanyId
    };
    if (currentConversationId) {
      payload.conversationId = currentConversationId;
    }
    
    // Use streaming API for real-time progress updates
    sendStreamingChat(payload);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const handleAddToDashboard = (chartSuggestion) => {
    setSelectedChartSuggestion(chartSuggestion);
    setSelectedChartType(chartSuggestion.type || 'bar');
    setChartName(chartSuggestion.name || 'AI Generated Chart');
    setSelectedDashboard('');
    setShowAddToDashboard(true);
  };
  
  const handleConfirmAddToDashboard = async () => {
    if (!selectedDashboard || !selectedChartType || !chartName.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsAddingChart(true);
    
    try {
      // Create chart from AI suggestion
      const chartData = {
        name: chartName.trim(),
        type: selectedChartType,
        dashboardId: selectedDashboard,
        query: selectedChartSuggestion.query || '',
        description: `Generated by AI Assistant`,
        chartConfig: selectedChartSuggestion.config || {},
        aiGenerated: true
      };
      
      // If there's a data source, include it
      if (selectedChartSuggestion.dataSourceId) {
        chartData.dataSourceId = selectedChartSuggestion.dataSourceId;
      }
      
      await addChart(chartData);
      
      toast.success('Chart added to dashboard!');
      setShowAddToDashboard(false);
      setSelectedChartSuggestion(null);
    } catch (error) {
      console.error('Failed to add chart:', error);
      toast.error(error.response?.data?.error || 'Failed to add chart to dashboard');
    } finally {
      setIsAddingChart(false);
    }
  };
  
  const handleCloseAddToDashboard = () => {
    setShowAddToDashboard(false);
    setSelectedChartSuggestion(null);
    setSelectedDashboard('');
    setSelectedChartType('');
    setChartName('');
  };
  
  const startNewConversation = () => {
    setMessages([]);
    setCurrentConversationId(null);
    navigate('/ai');
  };
  
  const conversations = conversationsData?.data?.data || [];
  const models = modelsData?.data?.models || [];
  const providers = modelsData?.data?.providers || {};
  
  // Handle conversation selection on mobile (close sidebar after selection)
  const handleConversationSelect = (convId) => {
    setCurrentConversationId(convId);
    navigate(`/ai/${convId}`);
    setSidebarOpen(false); // Close sidebar on mobile after selection
  };

  const handleNewChat = () => {
    startNewConversation();
    setSidebarOpen(false); // Close sidebar on mobile
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex animate-fade-in relative">
      {/* Mobile sidebar backdrop */}
      <Transition
        show={sidebarOpen}
        as={Fragment}
        enter="transition-opacity ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      </Transition>

      {/* Sidebar - Hidden on mobile, shown as overlay when toggled */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-surface-900 border-r border-surface-200 dark:border-surface-800 flex flex-col
        transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:z-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Mobile sidebar header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-800">
          <span className="font-display font-semibold text-surface-900 dark:text-white">Conversations</span>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
          >
            <XMarkIcon className="w-5 h-5 text-surface-500" />
          </button>
        </div>

        <div className="p-4">
          <button onClick={handleNewChat} className="btn-primary w-full">
            <PlusIcon className="w-5 h-5 mr-2" />
            New Chat
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto px-2">
          {noCompanySelected ? (
            <div className="text-center py-8 px-4">
              <p className="text-surface-500 text-sm">
                Select a company to view conversations
              </p>
            </div>
          ) : conversations.length === 0 ? (
            <p className="text-center text-surface-500 text-sm py-8">
              No conversations yet
            </p>
          ) : (
            <div className="space-y-1">
              {conversations.map(conv => (
                <div
                  key={conv._id}
                  className={`group flex items-center rounded-lg transition-colors ${
                    currentConversationId === conv._id
                      ? 'bg-primary-100 dark:bg-primary-900'
                      : 'hover:bg-surface-100 dark:hover:bg-surface-800'
                  }`}
                >
                  <button
                    onClick={() => handleConversationSelect(conv._id)}
                    className={`flex-1 text-left px-3 py-2 text-sm truncate ${
                      currentConversationId === conv._id
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-surface-600 dark:text-surface-400'
                    }`}
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4 inline mr-2" />
                    {conv.title}
                  </button>
                  <button
                    onClick={(e) => handleDeleteConversation(e, conv._id)}
                    className="p-2 opacity-0 group-hover:opacity-100 lg:group-hover:opacity-100 transition-opacity text-surface-400 hover:text-red-500 dark:text-surface-500 dark:hover:text-red-400"
                    title="Delete conversation"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header bar */}
        <div className="p-3 sm:p-4 border-b border-surface-200 dark:border-surface-800 flex items-center gap-2 sm:gap-4">
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 flex-shrink-0"
            aria-label="Open conversations"
          >
            <Bars3Icon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
          </button>

          {/* Hidden model selectors - keeping state for backend compatibility */}
          <div className="hidden">
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            >
              {Object.entries(providers).map(([key, value]) => (
                <option key={key} value={key}>{value.name}</option>
              ))}
            </select>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              {models
                .filter(m => m.provider === provider)
                .map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
            </select>
          </div>
        </div>
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4 sm:space-y-6">
          {noCompanySelected ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
                <SparklesIcon className="w-7 h-7 sm:w-8 sm:h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-lg sm:text-xl font-display font-bold text-surface-900 dark:text-white mb-2">
                Select a Company
              </h2>
              <p className="text-sm sm:text-base text-surface-500 dark:text-surface-400 max-w-md">
                Please select a company from the top navigation bar to start chatting with the AI Assistant.
              </p>
            </div>
          ) : messages.length === 0 ? (
            <SampleQuestions onQuestionClick={(question) => setMessage(question)} />
          ) : (
            messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 sm:gap-4 ${msg.role === 'assistant' ? '' : 'flex-row-reverse'}`}>
                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'assistant' 
                    ? 'bg-gradient-to-br from-primary-500 to-accent-500' 
                    : 'bg-surface-200 dark:bg-surface-700'
                }`}>
                  {msg.role === 'assistant' 
                    ? <SparklesIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    : <UserIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-surface-600 dark:text-surface-400" />
                  }
                </div>
                
                <div className={`max-w-[85%] sm:max-w-3xl ${msg.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block px-3 py-2 sm:px-4 sm:py-3 rounded-2xl ${
                    msg.role === 'assistant'
                      ? 'bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-white'
                      : 'bg-primary-600 text-white'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:my-2 prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-strong:text-primary-600 dark:prose-strong:text-primary-400 prose-code:bg-surface-200 dark:prose-code:bg-surface-700 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-surface-200 dark:prose-pre:bg-surface-900 prose-pre:text-surface-800 dark:prose-pre:text-surface-200 prose-pre:border prose-pre:border-surface-300 dark:prose-pre:border-surface-600 prose-pre:rounded-lg prose-pre:overflow-x-auto [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit text-sm sm:text-base">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap text-sm sm:text-base">{msg.content}</p>
                    )}
                  </div>
                  
                  {/* Export button for assistant messages */}
                  {msg.role === 'assistant' && (
                    <div className="mt-2 flex items-center gap-2">
                      <ExportDropdown messageContent={msg.content} messageIndex={i} />
                      <span className="text-xs text-surface-400 dark:text-surface-500">Export response</span>
                    </div>
                  )}
                  
                  {/* Chart suggestion with preview */}
                  {msg.chartSuggestion && (
                    <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-surface-50 dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-2 sm:mb-3">
                        <ChartBarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary-500" />
                        <span className="font-medium text-sm sm:text-base text-surface-900 dark:text-white">
                          {msg.chartSuggestion.name || 'Chart Preview'}
                        </span>
                        <span className="text-xs sm:text-sm text-surface-500 bg-surface-200 dark:bg-surface-700 px-2 py-0.5 rounded-full">
                          {msg.chartSuggestion.type}
                        </span>
                      </div>
                      
                      {/* Chart Preview */}
                      {msg.chartData && msg.chartData.length > 0 ? (
                        <div className="mb-3 rounded-lg bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 overflow-hidden">
                          {msg.chartSuggestion.type === 'table' ? (
                            // Render table for table type
                            <div className="overflow-x-auto max-h-64">
                              <table className="min-w-full text-xs">
                                <thead className="bg-surface-100 dark:bg-surface-800 sticky top-0">
                                  <tr>
                                    {Object.keys(msg.chartData[0] || {}).map((key) => (
                                      <th key={key} className="px-3 py-2 text-left font-medium text-surface-600 dark:text-surface-300 border-b border-surface-200 dark:border-surface-700">
                                        {key}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {msg.chartData.slice(0, 10).map((row, idx) => (
                                    <tr key={idx} className="hover:bg-surface-50 dark:hover:bg-surface-800">
                                      {Object.values(row).map((val, vidx) => (
                                        <td key={vidx} className="px-3 py-2 text-surface-700 dark:text-surface-300 border-b border-surface-100 dark:border-surface-800">
                                          {typeof val === 'number' ? val.toLocaleString() : String(val)}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {msg.chartData.length > 10 && (
                                <div className="px-3 py-2 text-xs text-surface-500 bg-surface-50 dark:bg-surface-800 border-t border-surface-200 dark:border-surface-700">
                                  Showing 10 of {msg.chartData.length} rows
                                </div>
                              )}
                            </div>
                          ) : (
                            // Render chart for other types
                            (() => {
                              const chartOption = buildChartPreviewOption(msg.chartData, msg.chartSuggestion.type, msg.chartSuggestion.config);
                              return chartOption ? (
                                <div className="h-64 p-2">
                                  <ReactECharts
                                    option={chartOption}
                                    style={{ height: '100%', width: '100%' }}
                                    opts={{ renderer: 'svg' }}
                                  />
                                </div>
                              ) : (
                                <div className="h-32 flex items-center justify-center text-surface-500 text-sm">
                                  Unable to render chart preview
                                </div>
                              );
                            })()
                          )}
                        </div>
                      ) : msg.chartSuggestion.query && (
                        // Fallback: Show query if no data available
                        <div className="mb-3 p-2 bg-surface-100 dark:bg-surface-900 rounded text-xs font-mono text-surface-600 dark:text-surface-400 overflow-x-auto">
                          <code>{msg.chartSuggestion.query.substring(0, 150)}...</code>
                        </div>
                      )}
                      
                      <button 
                        onClick={() => handleAddToDashboard(msg.chartSuggestion)}
                        className="btn-primary btn-sm text-xs sm:text-sm"
                      >
                        <PlusIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                        Add Chart to Dashboard
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          
          {isStreaming && (
            <ThinkingChain currentStep={thinkingStep} elapsedTime={elapsedTime} stepDetails={stepDetails} />
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div className="p-3 sm:p-4 border-t border-surface-200 dark:border-surface-800">
          <div className="flex gap-2 sm:gap-4">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={noCompanySelected ? "Select a company to start chatting..." : "Ask anything about your data..."}
              rows={1}
              className="flex-1 input resize-none text-sm sm:text-base"
              disabled={noCompanySelected}
            />
            <button
              onClick={handleSend}
              disabled={!message.trim() || isStreaming || noCompanySelected}
              className="btn-primary px-3 sm:px-4 flex-shrink-0"
              title={`Send message ${formatCost('ai_chat')}`}
            >
              <PaperAirplaneIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              {formatCost('ai_chat') && (
                <span className="ml-1 text-xs opacity-75 hidden sm:inline">{formatCost('ai_chat')}</span>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Add to Dashboard Modal */}
      <Transition appear show={showAddToDashboard} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleCloseAddToDashboard}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-surface-900 shadow-2xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-surface-200 dark:border-surface-800">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                        <ChartBarIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <Dialog.Title className="text-lg font-display font-semibold text-surface-900 dark:text-white">
                        Add Chart to Dashboard
                      </Dialog.Title>
                    </div>
                    <button 
                      onClick={handleCloseAddToDashboard}
                      className="p-1 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
                    >
                      <XMarkIcon className="w-5 h-5 text-surface-500" />
                    </button>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 space-y-5">
                    {/* Chart Name */}
                    <div>
                      <label className="label">Chart Name</label>
                      <input
                        type="text"
                        value={chartName}
                        onChange={(e) => setChartName(e.target.value)}
                        placeholder="Enter chart name"
                        className="input"
                      />
                    </div>
                    
                    {/* Select Dashboard */}
                    <div>
                      <label className="label">Select Dashboard</label>
                      {noCompanySelected ? (
                        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <p className="text-sm text-amber-700 dark:text-amber-300">
                            <strong>No company selected.</strong> Please select a company from the top navigation bar first to see available dashboards.
                          </p>
                        </div>
                      ) : (
                        <>
                          <select
                            value={selectedDashboard}
                            onChange={(e) => setSelectedDashboard(e.target.value)}
                            className="input"
                          >
                            <option value="">Choose a dashboard...</option>
                            {dashboardsData?.data?.data?.map(dashboard => (
                              <option key={dashboard._id} value={dashboard._id}>
                                {dashboard.name}
                              </option>
                            ))}
                          </select>
                          {(!dashboardsData?.data?.data || dashboardsData.data.data.length === 0) && (
                            <p className="text-sm text-surface-500 mt-1">
                              No dashboards found. Create a dashboard first.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Chart Type Selection */}
                    <div>
                      <label className="label">Chart Type</label>
                      <p className="text-sm text-surface-500 mb-2">
                        AI suggested: <span className="font-medium text-primary-600">{selectedChartSuggestion?.type || 'bar'}</span>
                      </p>
                      <div className="grid grid-cols-5 gap-2">
                        {chartTypes.map(type => (
                          <button
                            key={type.id}
                            onClick={() => setSelectedChartType(type.id)}
                            className={`p-3 rounded-xl border-2 text-center transition-all hover:border-primary-500 ${
                              selectedChartType === type.id
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950'
                                : 'border-surface-200 dark:border-surface-700'
                            }`}
                          >
                            <span className="text-xl block">{type.icon}</span>
                            <span className="text-xs font-medium text-surface-600 dark:text-surface-400 mt-1 block">
                              {type.name.split(' ')[0]}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* SQL Preview */}
                    {selectedChartSuggestion?.query && (
                      <div>
                        <label className="label">SQL Query</label>
                        <div className="p-3 bg-surface-100 dark:bg-surface-800 rounded-lg text-xs font-mono text-surface-600 dark:text-surface-400 overflow-x-auto max-h-32 overflow-y-auto">
                          <pre className="whitespace-pre-wrap">{selectedChartSuggestion.query}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Footer */}
                  <div className="flex items-center justify-end gap-3 p-6 border-t border-surface-200 dark:border-surface-800">
                    <button
                      onClick={handleCloseAddToDashboard}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmAddToDashboard}
                      disabled={!selectedDashboard || !selectedChartType || !chartName.trim() || isAddingChart || noCompanySelected}
                      className="btn-primary"
                    >
                      {isAddingChart ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      ) : (
                        <CheckIcon className="w-5 h-5 mr-2" />
                      )}
                      Add to Dashboard
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      
      {/* Query Patterns Helper */}
      {!noCompanySelected && (
        <QueryPatterns
          isOpen={showQueryPatterns}
          onToggle={() => setShowQueryPatterns(!showQueryPatterns)}
          onPatternClick={(pattern) => {
            setMessage(pattern);
            setShowQueryPatterns(false);
          }}
        />
      )}
    </div>
  );
}

